import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
} from 'react-native';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';
import { ConvenienceFeesSection } from '@/components/residents/payments/shared/ConvenienceFeesSection';
import { CurrencyInput } from '@/components/residents/payments/shared/CurrencyInput';
import { RadioButton } from './shared';
import { getNextMonthFirstDay } from '@/utils/residents/date';
import { BottomSheetModal } from '@/components/residents/marketplace/shared/BottomSheetModal';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { SVG_COMPONENTS } from '@/assets/admin';
import type { FeeStructure } from '@/types/residents/payment';

const InfoIcon = RESIDENTS_SVG_COMPONENTS.INFORMATION_CIRCLE;
const ChevronDown = SVG_COMPONENTS.CHEVRON_DOWN;

interface AutopayEditContentProps {
  autopay: {
    amount?: number;
    isFullAmount?: boolean;
    startDate?: string;
    endDate?: string;
    paymentMethodLabel?: string;
  };
  leaseEndDate?: string;
  feeStructure: FeeStructure;
  onChangePaymentMethod: () => void;
  onAddPaymentMethod?: () => void;
  hasPaymentMethod?: boolean;
  // Form state - controlled by parent
  amountType: 'balance' | 'other';
  customAmount: string;
  dateRangeType: 'leaseEnd' | 'custom';
  onAmountTypeChange: (type: 'balance' | 'other') => void;
  onCustomAmountChange: (value: string) => void;
  onDateRangeTypeChange: (type: 'leaseEnd' | 'custom') => void;
  /** Ref for the custom amount TextInput (managed by parent via useAmountInput) */
  inputRef?: React.RefObject<TextInput | null>;
  // Custom date range state
  firstPaymentMonth?: string;
  lastPaymentMonth?: string;
  onFirstPaymentPress?: () => void;
  onLastPaymentPress?: () => void;
}

/**
 * Autopay Edit Content
 * Matches Figma design 3. - Autopay set up or edit screen
 */
export const AutopayEditContent: React.FC<AutopayEditContentProps> = ({
  autopay,
  leaseEndDate,
  feeStructure,
  onChangePaymentMethod,
  onAddPaymentMethod,
  hasPaymentMethod = true,
  amountType,
  customAmount,
  dateRangeType,
  onAmountTypeChange,
  onCustomAmountChange,
  onDateRangeTypeChange,
  firstPaymentMonth,
  lastPaymentMonth,
  inputRef,
  onFirstPaymentPress,
  onLastPaymentPress,
}) => {
  const MARKETPLACE_COLORS = useMarketplaceColors();
  // Tooltip state for date range info
  const [showDateRangeTooltip, setShowDateRangeTooltip] = useState(false);

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        {/* Amount Section */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: MARKETPLACE_COLORS.textPrimary }]}>Amount</Text>
          <View style={styles.optionsContainer}>
            {/* Full balance option */}
            <RadioButton
              selected={amountType === 'balance'}
              onPress={() => onAmountTypeChange('balance')}
              label="Full balance"
            />

            {/* Other amount option with input - input always visible per Figma */}
            <TouchableOpacity
              style={styles.otherAmountRow}
              onPress={() => onAmountTypeChange('other')}
              activeOpacity={0.7}
            >
              <RadioButton
                selected={amountType === 'other'}
                onPress={() => onAmountTypeChange('other')}
                label="Other amount"
              />
              <CurrencyInput
                ref={inputRef}
                containerStyle={StyleSheet.flatten([styles.amountInputContainer, { backgroundColor: MARKETPLACE_COLORS.inputBackground, borderColor: MARKETPLACE_COLORS.inputBorder }])}
                currencySymbolStyle={StyleSheet.flatten([styles.dollarSign, { color: MARKETPLACE_COLORS.textSecondary }])}
                inputStyle={StyleSheet.flatten([styles.amountInput, { color: MARKETPLACE_COLORS.textSecondary }])}
                value={customAmount}
                onChangeText={onCustomAmountChange}
                placeholder="0.00"
                placeholderTextColor={MARKETPLACE_COLORS.textSecondary}
                editable={amountType === 'other'}
              />
            </TouchableOpacity>
          </View>
        </View>

        {/* Date Range Section */}
        <View style={styles.section}>
          <View style={styles.sectionTitleRow}>
            <Text style={[styles.sectionTitle, { color: MARKETPLACE_COLORS.textPrimary }]}>Date range</Text>
            <TouchableOpacity
              onPress={() => setShowDateRangeTooltip(true)}
              activeOpacity={0.7}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              {InfoIcon && (
                <InfoIcon width={20} height={20} color={MARKETPLACE_COLORS.textPrimary} />
              )}
            </TouchableOpacity>
          </View>
          <View style={styles.optionsContainer}>
            <RadioButton
              selected={dateRangeType === 'leaseEnd'}
              onPress={() => onDateRangeTypeChange('leaseEnd')}
              label={`From ${getNextMonthFirstDay()} to lease end`}
            />
            <RadioButton
              selected={dateRangeType === 'custom'}
              onPress={() => onDateRangeTypeChange('custom')}
              label="Custom"
            />
            {/* Custom Date Range Dropdowns */}
            {dateRangeType === 'custom' && (
              <View style={styles.customDateRangeRow}>
                {/* First Payment Dropdown */}
                <TouchableOpacity
                  style={[styles.dateDropdown, { backgroundColor: MARKETPLACE_COLORS.inputBackground, borderColor: MARKETPLACE_COLORS.inputBorder }]}
                  onPress={onFirstPaymentPress}
                  activeOpacity={0.7}
                >
                  <View style={styles.dateDropdownContent}>
                    {firstPaymentMonth ? (
                      <>
                        <Text style={[styles.dateDropdownLabel, { color: MARKETPLACE_COLORS.textSecondary }]}>First payment</Text>
                        <Text style={[styles.dateDropdownValue, { color: MARKETPLACE_COLORS.textPrimary }]}>{firstPaymentMonth}</Text>
                      </>
                    ) : (
                      <Text style={[styles.dateDropdownPlaceholder, { color: MARKETPLACE_COLORS.textSecondary }]}>First payment</Text>
                    )}
                  </View>
                  {ChevronDown && (
                    <ChevronDown width={20} height={20} color={MARKETPLACE_COLORS.textSecondary} />
                  )}
                </TouchableOpacity>

                {/* Last Payment Dropdown */}
                <TouchableOpacity
                  style={[styles.dateDropdown, { backgroundColor: MARKETPLACE_COLORS.inputBackground, borderColor: MARKETPLACE_COLORS.inputBorder }]}
                  onPress={onLastPaymentPress}
                  activeOpacity={0.7}
                >
                  <View style={styles.dateDropdownContent}>
                    {lastPaymentMonth ? (
                      <>
                        <Text style={[styles.dateDropdownLabel, { color: MARKETPLACE_COLORS.textSecondary }]}>Last payment</Text>
                        <Text style={[styles.dateDropdownValue, { color: MARKETPLACE_COLORS.textPrimary }]}>{lastPaymentMonth}</Text>
                      </>
                    ) : (
                      <Text style={[styles.dateDropdownPlaceholder, { color: MARKETPLACE_COLORS.textSecondary }]}>Last payment</Text>
                    )}
                  </View>
                  {ChevronDown && (
                    <ChevronDown width={20} height={20} color={MARKETPLACE_COLORS.textSecondary} />
                  )}
                </TouchableOpacity>
              </View>
            )}
          </View>
        </View>

        {/* Payment Method Section */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: MARKETPLACE_COLORS.textPrimary }]}>Payment method</Text>
          <View style={styles.paymentMethodRow}>
            {hasPaymentMethod && autopay.paymentMethodLabel ? (
              <>
                <Text style={[styles.paymentMethodText, { color: MARKETPLACE_COLORS.textPrimary }]}>
                  {autopay.paymentMethodLabel}
                </Text>
                <TouchableOpacity onPress={onChangePaymentMethod} activeOpacity={0.7}>
                  <Text style={[styles.changeLink, { color: MARKETPLACE_COLORS.accentBlue }]}>Change</Text>
                </TouchableOpacity>
              </>
            ) : (
              <TouchableOpacity
                onPress={onAddPaymentMethod || onChangePaymentMethod}
                activeOpacity={0.7}
              >
                <Text style={[styles.addPaymentMethodLink, { color: MARKETPLACE_COLORS.accentBlue }]}>Add a payment method</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>

      {/* Convenience Fees Section */}
      <ConvenienceFeesSection
        feeStructure={feeStructure}
        variant="mobile"
        showHeader={true}
      />

      {/* Date Range Tooltip Bottom Sheet */}
      <BottomSheetModal
        visible={showDateRangeTooltip}
        onClose={() => setShowDateRangeTooltip(false)}
        showCloseButton={true}
      >
        <Text style={[styles.tooltipText, { color: MARKETPLACE_COLORS.textPrimary }]}>
          Your payments will be processed on the first day of each month
        </Text>
      </BottomSheetModal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    paddingHorizontal: 24,
    paddingVertical: 24,
    gap: 16,
  },
  section: {
    gap: 8,
  },
  sectionTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 24,
  },
  optionsContainer: {
    paddingLeft: 16,
    gap: 8,
  },

  // Other amount row with input
  otherAmountRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  amountInputContainer: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 16,
    height: 40,
    width: 128,
  },
  dollarSign: {
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 17.5,
    marginRight: 8,
  },
  amountInput: {
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 17.5,
    textAlign: 'right',
  },

  // Custom date range dropdowns
  customDateRangeRow: {
    flexDirection: 'row',
    gap: 20,
    marginTop: 8,
  },
  dateDropdown: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 16,
    height: 40,
  },
  dateDropdownContent: {
    flex: 1,
    justifyContent: 'center',
  },
  dateDropdownPlaceholder: {
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 17.5,
  },
  dateDropdownLabel: {
    fontSize: 10,
    fontWeight: '600',
    lineHeight: 12.5,
  },
  dateDropdownValue: {
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 17.5,
  },

  // Payment method row
  paymentMethodRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: 16,
  },
  paymentMethodText: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 21,
  },
  changeLink: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 21,
  },
  addPaymentMethodLink: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 21,
  },

  // Tooltip
  tooltipText: {
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 21,
    minHeight: 200,
  },
});

export default AutopayEditContent;
